import React, { useState } from "react";
import { fetchJson } from "../../lib/api";
import type { CreateHousePayload } from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Input } from "../input";

export const CreateHouseForm = ({
  title,
  onCreated,
}: {
  title: string;
  onCreated: () => void;
}) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError(t("house.nameRequired", "House name is required."));
      return;
    }
    setIsSubmitting(true);
    try {
      const payload: CreateHousePayload = { name };
      const response = await fetchJson<{ id: number }>("/api/houses", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      void response;
      setName("");
      setIsOpen(false);
      onCreated();
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : t("house.createFailed", "Failed to create"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => {
          setError(null);
          setIsOpen(true);
        }}
      >
        {t("house.create", "Create house")}
      </Button>
      {isOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{title}</CardTitle>
              <p className="text-sm text-muted">
                {t(
                  "house.createDescription",
                  "Create the house first, then connect any booking channels.",
                )}
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-ink" htmlFor="house-name">
                    {t("house.nameLabel", "House name")}
                  </label>
                  <Input
                    id="house-name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={t("house.namePlaceholder", "Coastal Cabin")}
                    required
                  />
                </div>
                {error ? <p className="text-sm text-error">{error}</p> : null}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setIsOpen(false);
                      setError(null);
                    }}
                  >
                    {t("common.cancel", "Cancel")}
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting
                      ? t("common.saving", "Saving...")
                      : t("house.create", "Create house")}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </>
  );
};
